import axios from "axios";
import { gaEvent } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const FALLBACK_CONTENT_V1 = {
  ctaText: "Enter the Leaderboard",
  ctaUrl: "https://affiliate-arena.emergent.host",
  hero: {
    eyebrow: "Emergent's Game Room",
    badge: "Invite Only · No Ranking",
    maxPayout: "$1,000",
    openPrompt: "Open the book to learn how to win up to",
    headlinePre: "The easiest",
    headlineAmount: "$30",
    headlinePost: "you'll make all week.",
    sublinePre: "…and up to",
    sublineAmount: "$1,000",
    sublinePost: "if you feel like trying.",
    atmosphere: "No ranking. Pure conversions. Show up, cash in.",
    marquee: "$30 sign-in bonus · up to $1,000 for conversions · everyone qualifies",
  },
  rules: {
    title: "The Bonus Rules",
    subtitle: "No ranking. Pure conversions. The more you bring, the more you earn.",
    signinLabel: "Sign-in Bonus",
    signinAmount: "$30",
    signinText: "instantly, just for joining the leaderboard.",
    footnote: "You earn the bonus for the highest conversion tier you reach, and the $30 sign-in bonus is on top.",
    tiers: [
      { label: "3+ conversions", amount: 200 },
      { label: "6+ conversions", amount: 500 },
      { label: "10+ conversions", amount: 1000 },
    ],
  },
  finalCta: {
    headline: "Show up. Cash in.",
    subheading: "No ranking to sweat. Every conversion counts, up to $1,000, plus your $30 sign-in bonus on top.",
    ctaLabel: "Enter the Leaderboard",
    claimTitle: "How to Claim",
    steps: [
      { icon: "Unlock", title: "Sign up", desc: "Sign in to the leaderboard and get $30 instantly." },
      { icon: "Target", title: "Drive conversions", desc: "Every conversion counts toward your bonus tier." },
      { icon: "Trophy", title: "Get paid", desc: "Hit 3, 6, or 10+ conversions to unlock up to $1,000." },
    ],
  },
};

export const fetchContent = async (variant = "v1") => {
  const res = await axios.get(`${API}/content`, { params: { variant } });
  return res.data;
};

export const trackEvent = async (event, location, meta) => {
  const enriched = { ...(meta || {}), path: window.location.pathname };
  // Send to Google Analytics (non-blocking)
  gaEvent(event, { location, ...enriched });
  try {
    await axios.post(`${API}/track`, { event, location, meta: enriched });
  } catch (e) {
    // analytics is non-blocking
  }
};

// v1 fallback (Casino theme, fully admin-editable schema).
export const FALLBACK_CONTENT_V1_HUD = {
  ctaText: "Log in to Leaderboard",
  ctaUrl: "https://affiliate-arena.emergent.host",
  coinShower: true,
  howToEarnHeading: "How to earn",
  hero: {
    brandLead: "Emergent's",
    brandAccent: "Game Room",
    eyebrow: "Exclusive Invite · Emergent's Game Room",
    dates: "Live Aug 1 – Aug 10",
    startDate: "2026-08-01T00:00:00Z",
    startsInLabel: "Starts in",
    liveWord: "Live",
    dayLabel: "Day",
    winPre: "Win up to",
    maxTotal: "$2,000",
    signinChip: "$30 just for signing in",
    learnLabel: "Learn How to Earn",
  },
  signin: {
    title: "Sign-in Bonus",
    amount: 30,
    body: "You can earn $30 just by signing up.",
    note: "Only if you log in during the campaign window.",
  },
  ranks: {
    cardTitle: "Ranking and Milestones",
    rankSuffix: "Rank",
    items: [
      { rank: "#1", label: "Champion", qualifier: "", amount: 1000 },
    ],
  },
  ladders: {
    tierSuffix: "conversions",
    single: {
      name: "Conversion Tiers",
      subtitle: "Every conversion counts",
      tiers: [
        { count: "3+", amount: 200 },
        { count: "6+", amount: 500 },
        { count: "10+", amount: 1000 },
      ],
    },
  },
  guidelines: {
    title: "Guidelines",
    items: [
      "Bonuses are paid only if you log into the leaderboard.",
      "Reach out to us in case you face any issue.",
      "Finish **#1** rank on the leaderboard for **$1000** and stack it by hitting the milestones and get a chance to win up to **$2000**.",
    ],
  },
};

// Fallback content so the page never renders blank if the API is unreachable.
export const FALLBACK_CONTENT_V2 = {
  ctaText: "Log in to Leaderboard",
  ctaUrl: "https://affiliate-arena.emergent.host",
  screen2Heading: "How to Earn",
  ui: {
    selectModule: "Select a module",
    tapToOpen: "Tap to open",
    back: "Back",
    conversionsLabel: "Conversions",
    rankWord: "Rank",
  },
  hero: {
    brandLead: "Emergent's",
    brandAccent: "Game Room",
    eyebrow: "Exclusive Invite · Emergent's Game Room",
    dates: "Live Aug 1 – Aug 10",
    startDate: "2026-08-01T00:00:00Z",
    startsInLabel: "Starts in",
    liveWord: "Live",
    dayLabel: "Day",
    hook: "You earned the invite. Now earn the prize.",
    winPre: "Win up to",
    maxTotal: "$21,000",
    supporting: "Three prizes. They stack.",
    signinBonus: "Win $20 just for signing in",
    learnLabel: "Learn How to Earn",
    ticker: "Milestones stack with leaderboard prizes · Premium pays more",
  },
  ranks: {
    title: "Leaderboard Ranks",
    teaser: "See what topping the board pays.",
    subtitle: "Ranked by total conversions (premium + non-premium).",
    items: [
      { rank: "#1", label: "Champion", qualifier: "", amount: 7000 },
      { rank: "#2", label: "Runner-up", qualifier: "up to", amount: 4000 },
      { rank: "#3", label: "Third Place", qualifier: "up to", amount: 2000 },
    ],
  },
  ladders: {
    title: "Milestones",
    teaser: "Earn bonuses as your conversions climb.",
    subtitle: "Every conversion counts, from premium and non-premium regions alike. In each, you earn the highest tier you hit, and the two stack. Each caps at $7,000.",
    premium: {
      name: "Premium Geo",
      subtitle: "North America, Europe, Oceania (AU & NZ)",
      tiers: [
        { count: 10, amount: 500 },
        { count: 25, amount: 1500 },
        { count: 50, amount: 3000 },
        { count: 75, amount: 4000 },
        { count: 100, amount: 5000 },
        { count: 200, amount: 7000 },
      ],
    },
    nonPremium: {
      name: "Non-Premium Geo",
      subtitle: "Africa, South America, Asia, etc.",
      tiers: [
        { count: 10, amount: 200 },
        { count: 25, amount: 500 },
        { count: 50, amount: 1000 },
        { count: 75, amount: 1500 },
        { count: 100, amount: 2000 },
        { count: 200, amount: 3000 },
        { count: 400, amount: 5000 },
        { count: 600, amount: 7000 },
      ],
    },
  },
  steps: {
    title: "How to Claim",
    teaser: "Three steps to get paid.",
    items: [
      { title: "Log in", desc: "Sign into the leaderboard and earn $20. (You will receive any kind of bonus only if you log into the leaderboard)" },
      { title: "Drive conversions", desc: "Each one climbs your rank and both ladders. Premium pays more." },
      { title: "Get paid", desc: "Stack leaderboard + premium + non-premium. Up to $21,000." },
    ],
  },
  finalCta: {
    ctaLabel: "Enter the Leaderboard",
  },
};

// v3 fallback (single ladder, no premium/geo) so no $21,000 flash before the API resolves.
export const FALLBACK_CONTENT_V3 = {
  ctaText: "Log in to Leaderboard",
  ctaUrl: "https://affiliate-arena.emergent.host",
  screen2Heading: "How to Earn",
  ui: {
    selectModule: "Select a module",
    tapToOpen: "Tap to open",
    back: "Back",
    conversionsLabel: "Conversions",
    rankWord: "Rank",
  },
  hero: {
    brandLead: "Emergent's",
    brandAccent: "Game Room",
    eyebrow: "Exclusive Invite · Emergent's Game Room",
    dates: "Live Aug 1 – Aug 10",
    startDate: "2026-08-01T00:00:00Z",
    startsInLabel: "Starts in",
    liveWord: "Live",
    dayLabel: "Day",
    hook: "You earned the invite. Now earn the prize.",
    winPre: "Win up to",
    maxTotal: "$15,000",
    supporting: "Two prizes. They stack.",
    signinBonus: "Win $25 just for signing in",
    learnLabel: "Learn How to Earn",
    ticker: "Milestones stack with leaderboard prizes",
  },
  ranks: {
    title: "Leaderboard Ranks",
    teaser: "See what topping the board pays.",
    subtitle: "Ranked by total conversions.",
    items: [
      { rank: "#1", label: "Champion", qualifier: "", amount: 5000 },
      { rank: "#2", label: "Runner-up", qualifier: "up to", amount: 3000 },
      { rank: "#3", label: "Third Place", qualifier: "up to", amount: 2000 },
      { rank: "#4", label: "Fourth Place", qualifier: "up to", amount: 1000 },
    ],
  },
  ladders: {
    title: "Milestones",
    teaser: "Earn bonuses as your conversions climb.",
    subtitle: "Every conversion counts. You earn the highest tier you hit, and it stacks with your leaderboard prize.",
    single: {
      name: "Milestone Ladder",
      subtitle: "Every conversion counts",
      tiers: [
        { count: 5, amount: 300 },
        { count: 15, amount: 700 },
        { count: 30, amount: 1000 },
        { count: 50, amount: 2000 },
        { count: 75, amount: 3000 },
        { count: 100, amount: 5000 },
        { count: 150, amount: 7000 },
        { count: 250, amount: 10000 },
      ],
    },
  },
  steps: {
    title: "How to Claim",
    teaser: "Three steps to get paid.",
    items: [
      { title: "Log in", desc: "Sign into the leaderboard and earn $25. (You will receive any kind of bonus only if you log into the leaderboard)" },
      { title: "Drive conversions", desc: "Every conversion climbs your rank and the milestone ladder." },
      { title: "Get paid", desc: "Stack your leaderboard prize with the milestone ladder. Up to $15,000." },
    ],
  },
  finalCta: {
    ctaLabel: "Enter the Leaderboard",
  },
};

// v4 fallback (Impact campaign, Sunset Amber skin) — same shape as v2.
export const FALLBACK_CONTENT_V4 = JSON.parse(JSON.stringify(FALLBACK_CONTENT_V2));

// v5 fallback — "Earn More Zone" partner bonus infographic (clean table layout, Sunset skin).
export const FALLBACK_CONTENT_V5 = {
  ctaText: "Read Full Terms",
  ctaUrl: "https://affiliate-arena.emergent.host/",
  hero: {
    brandLead: "Emergent's",
    brandAccent: "Game Room",
    dates: "Live Sep 3 – Sep 9",
    startDate: "2026-09-03T00:00:00Z",
    eyebrow: "",
    liveWord: "Live",
    dayLabel: "Day",
    startsInLabel: "Starts in",
    hook: "",
    winPre: "Win up to",
    maxTotal: "$23,000+",
    supporting: "Milestones, daily bonuses, and the podium.",
    signinBonus: "Win $20 just by signing up on the leaderboard.",
    loginLabel: "Log into the Leaderboard",
    learnLabel: "See How to Earn",
    ctaNote: "Partners will only be eligible for any sort of bonus if they log into the leaderboard.",
  },
  ranks: {
    title: "Rankings",
    teaser: "See what topping the board pays.",
    subtitle: "Top 3 by total conversions, plus a Premium Champion crown for the top NA / EU / Oceania partner.",
    items: [
      { rank: "1", label: "Champion", qualifier: "", amount: 3500 },
      { rank: "2", label: "Runner-up", qualifier: "", amount: 2000 },
      { rank: "3", label: "Third Place", qualifier: "", amount: 1000 },
    ],
  },
  ladders: {
    title: "Geo Ladders + Daily",
    teaser: "Your geo ladder plus daily bonuses.",
    subtitle: "",
    premium: {
      name: "Premium Geo",
      subtitle: "North America, Europe, Oceania (AU & NZ)",
      tiers: [
        { count: 5, amount: 250 },
        { count: 10, amount: 600 },
        { count: 15, amount: 1000 },
        { count: 20, amount: 1600 },
        { count: 35, amount: 3500 },
      ],
    },
    nonPremium: {
      name: "Non-Premium Geo",
      subtitle: "Everywhere else",
      tiers: [
        { count: 5, amount: 100 },
        { count: 10, amount: 300 },
        { count: 15, amount: 500 },
        { count: 25, amount: 900 },
        { count: 40, amount: 1500 },
        { count: 55, amount: 2500 },
        { count: 75, amount: 4000 },
        { count: 100, amount: 6000 },
        { count: 125, amount: 9000 },
        { count: 150, amount: 12000 },
      ],
    },
    daily: {
      title: "Daily Bonus Ladder",
      note: "Highest rung only, resets daily, counted on total conversions across all geos.",
      rows: [
        { item: "7 conversions a day", value: "$150" },
        { item: "10 conversions a day", value: "$350" },
        { item: "15 conversions a day", value: "$700" },
      ],
    },
  },
  steps: {
    title: "Daily Bonuses & Rules",
    teaser: "The extras and the fine print.",
    items: [
      { title: "Daily bonus", desc: "5 Paid Customers in a day = $150. 10 in a day = $350." },
      { title: "Sign-in bonus", desc: "One-time $20 on your first login. No daily sign-in bonus." },
      { title: "Paid Customers only", desc: "Bonuses pay on PAID Customers only: Revenue Share + Subscription actions, net of reversals. Sign-ups don't count." },
    ],
  },
  screen2Heading: "How to Earn",
  ui: {
    selectModule: "",
    tapToOpen: "Tap to open",
    back: "Back",
    conversionsLabel: "Paid Customers / week",
    rankWord: "Rank",
  },
  breakdown: {
    title: "How the #1 partner hits $23,920",
    teaser: "The math, stacked.",
    setup: "",
    setupAside: "",
    rows: [
      { label: "Non-Premium ladder", detail: "Top the Non-Premium ladder (150+) for $12K.", amount: "$12,000" },
      { label: "Premium ladder", detail: "Top the Premium ladder (35+) for $3.5K, both ladders stack.", amount: "$3,500" },
      { label: "Podium", detail: "#1 on the board", amount: "$3,500" },
      { label: "Daily bonuses", detail: "$700 a day (the top daily bonus) × 7 days = $4,900.", amount: "$4,900" },
      { label: "Sign-in", detail: "A one time payment made only if the partner signs up during the campaign window.", amount: "$20" },
    ],
    totalLabel: "Total",
    total: "$23,920",
    perCustomer: "Earn up to $130+ for every paying customer you bring in",
  },
  finalCta: { ctaLabel: "Read Full Terms" },
};

export const FALLBACK_CONTENT_V6 = JSON.parse(JSON.stringify(FALLBACK_CONTENT_V5));
FALLBACK_CONTENT_V6.hero.dates = "Live Sep 3 – Sep 9";
FALLBACK_CONTENT_V6.hero.startDate = "2026-09-03T00:00:00Z";
FALLBACK_CONTENT_V6.hero.maxTotal = "$17,000+";
FALLBACK_CONTENT_V6.hero.signinBonus = "Win $20 just by signing up on the leaderboard.";
FALLBACK_CONTENT_V6.hero.supporting = "Milestones, daily bonuses, and the podium.";
FALLBACK_CONTENT_V6.ranks.subtitle = "Top 2 by total Paid Customers.";
FALLBACK_CONTENT_V6.ranks.items = [
  { rank: "1", label: "Champion", qualifier: "", amount: 4000 },
  { rank: "2", label: "Runner-up", qualifier: "", amount: 2000 },
];
FALLBACK_CONTENT_V6.ladders = {
  title: "Daily & Milestone Bonuses",
  teaser: "The conversion ladder, growth bonus, and your daily bonuses.",
  subtitle: "",
  weekly: {
    title: "Conversion Ladder (Milestone, Paid Once)",
    colLabel: "Proj Conv",
    rule: "",
    rows: [
      { tier: "5+", bonus: "$50" },
      { tier: "20+", bonus: "$300" },
      { tier: "50+", bonus: "$1,200" },
      { tier: "75+", bonus: "$3,000" },
      { tier: "110+", bonus: "$5,000" },
      { tier: "140+", bonus: "$7,000" },
      { tier: "170+", bonus: "$8,500" },
      { tier: "200+", bonus: "$10,000" },
    ],
  },
  growth: {
    title: "Growth % Bonus",
    note: "Grow your total conversions vs. your last period. Minimum 20 conversions to qualify.",
    rows: [
      { tier: ">30%", bonus: "$100" },
      { tier: ">100%", bonus: "$400" },
      { tier: ">200%", bonus: "$1,000" },
    ],
  },
  daily: {
    title: "Daily Bonus Ladder",
    note: "",
    rows: [
      { item: "7 Paid Customers a day", value: "$100" },
      { item: "14 Paid Customers a day", value: "$200" },
      { item: "21 Paid Customers a day", value: "$350" },
    ],
  },
};
FALLBACK_CONTENT_V6.steps.items = [
  { title: "Daily bonus", desc: "Stacks every day for all 7 days: 7 Paid Customers = $100, 14 = $200, 21 = $350." },
  { title: "Sign-in bonus", desc: "One-time $20 for new partners on your first login. No daily sign-in bonus." },
  { title: "Paid Customers only", desc: "Bonuses pay on PAID Customers only: Revenue Share + Subscription actions, net of reversals. Sign-ups don't count." },
];
FALLBACK_CONTENT_V6.breakdown.title = "How the #1 partner hits $17,470";
FALLBACK_CONTENT_V6.breakdown.setup = "";
FALLBACK_CONTENT_V6.breakdown.setupAside = "";
FALLBACK_CONTENT_V6.breakdown.rows = [
  { label: "Podium", detail: "#1 on the board", amount: "$4,000" },
  { label: "Conversion ladder", detail: "Hit the highest conversions (200+) and bag $10K.", amount: "$10,000" },
  { label: "Growth bonus", detail: "Grow 200%+ over last period (20+ conversions to qualify).", amount: "$1,000" },
  { label: "Daily bonuses", detail: "$350 a day (the top daily bonus) × 7 days = $2,450.", amount: "$2,450" },
  { label: "Sign-in", detail: "A one time payment made only if the partner signs up during the campaign window.", amount: "$20" },
];
FALLBACK_CONTENT_V6.breakdown.total = "$17,470";
FALLBACK_CONTENT_V6.breakdown.perCustomer = "Earn up to $85+ for every paying customer you bring in";

export const FALLBACK_CONTENT_V7 = JSON.parse(JSON.stringify(FALLBACK_CONTENT_V6));
FALLBACK_CONTENT_V7.hero.maxTotal = "$39,000+";
FALLBACK_CONTENT_V7.hero.dates = "Live Sep 3 – Sep 9";
FALLBACK_CONTENT_V7.hero.startDate = "2026-09-03T00:00:00Z";
FALLBACK_CONTENT_V7.hero.signinBonus = "Win $20 just by signing up on the leaderboard.";
FALLBACK_CONTENT_V7.ranks.subtitle = "Top 2 by total Paid Customers.";
FALLBACK_CONTENT_V7.ranks.items = [
  { rank: "1", label: "Champion", qualifier: "", amount: 5000 },
  { rank: "2", label: "Runner-up", qualifier: "", amount: 3000 },
];
FALLBACK_CONTENT_V7.ladders = {
  title: "Milestones",
  teaser: "Earn bonuses as your conversions climb.",
  subtitle: "You compete in one geo: Premium or Non-Premium. Earn the highest tier you reach in your geo; it stacks with your podium prize and daily bonuses.",
  premium: {
    name: "Premium Geo",
    subtitle: "North America, Europe, Oceania (AU & NZ)",
    tiers: [
      { count: 10, amount: 200 },
      { count: 30, amount: 800 },
      { count: 50, amount: 2000 },
      { count: 100, amount: 4500 },
      { count: 120, amount: 6000 },
    ],
  },
  nonPremium: {
    name: "Non-Premium Geo",
    subtitle: "Africa, South America, Asia, etc.",
    tiers: [
      { count: 20, amount: 200 },
      { count: 50, amount: 700 },
      { count: 90, amount: 2000 },
      { count: 120, amount: 3000 },
      { count: 200, amount: 7000 },
      { count: 250, amount: 10000 },
      { count: 300, amount: 12500 },
      { count: 350, amount: 15000 },
      { count: 400, amount: 20000 },
    ],
  },
  daily: {
    title: "Daily Bonus Ladder",
    note: "Per day (average), stacks all 7 days.",
    rows: [
      { item: "15 a day", value: "$150" },
      { item: "35 a day", value: "$400" },
      { item: "45 a day", value: "$600" },
      { item: "60 a day", value: "$850" },
      { item: "70 a day", value: "$1,200" },
    ],
  },
};
FALLBACK_CONTENT_V7.ui.conversionsLabel = "Total Paid Customers";
FALLBACK_CONTENT_V7.breakdown.title = "How the #1 partner hits $39,420";
FALLBACK_CONTENT_V7.breakdown.rows = [
  { label: "Best ladder", detail: "Both geos stack: Non-Premium $20K (400+) plus Premium $6K (120+).", amount: "$26,000" },
  { label: "Daily bonuses", detail: "$1,200 a day (the top daily bonus) × 7 days = $8,400.", amount: "$8,400" },
  { label: "Sign-in", detail: "A one time payment made only if the partner signs up during the campaign window.", amount: "$20" },
  { label: "Podium", detail: "#1 on the board", amount: "$5,000" },
];
FALLBACK_CONTENT_V7.breakdown.total = "$39,420";
FALLBACK_CONTENT_V7.breakdown.perCustomer = "Every paying customer you refer climbs your ladder and your rank.";
// Preserve v7's own steps (active podium) so the v6 inactive-steps rewrite doesn't leak in.
FALLBACK_CONTENT_V7.steps = JSON.parse(JSON.stringify(FALLBACK_CONTENT_V5.steps));

export const FALLBACK_CONTENT_PODIUM = JSON.parse(JSON.stringify(FALLBACK_CONTENT_V7));
export const FALLBACK_CONTENT_SPRINT = JSON.parse(JSON.stringify(FALLBACK_CONTENT_V7));


// Fallback content so the page never renders blank if the API is unreachable.
export const FALLBACK_CONTENT = {
  ctaText: "Log in to Leaderboard",
  ctaUrl: "https://affiliate-arena.emergent.host",
  hero: {
    badge: "Invite Only · 7-Day Challenge",
    headline: "You are the chosen one",
    winLine: "1st prize: $10,000",
    winHighlight: "$10,000",
    winQualifier: "up to",
    curiosity: "Top the leaderboard and it's yours.",
    scrollCta: "Scroll to see how it works",
    signupBonus: "Win $10 just for signing up on the leaderboard!",
    signupBonusKeyword: "$10",
    campaignDates: "Live: 10 - 16 July",
    marquee: "Milestone rewards stack with top-rank prizes · everyone qualifies",
  },
  prizesIntro: {
    eyebrow: "Top Ranks",
    title: "The grand prizes",
    subtitle:
      "Finish #1 or #2 and the cash is locked in, on top of every milestone you hit.",
  },
  fixedBonuses: [
    { rank: 1, amount: 7000, label: "Champion", medal: "gold" },
    { rank: 2, amount: 4000, label: "Runner-Up", medal: "silver" },
  ],
  milestones: {
    eyebrow: "Milestone Rewards",
    title: "The milestone ladder",
    subtitle:
      "Hit subscription targets to unlock payouts. Everyone qualifies, and milestone rewards are separate from and stack with the top-rank prizes.",
    tiers: [
      { conversions: 10, amount: 200 },
      { conversions: 25, amount: 500 },
      { conversions: 50, amount: 1000 },
      { conversions: 75, amount: 1500 },
      { conversions: 100, amount: 2000 },
      { conversions: 200, amount: 3000 },
    ],
  },
  finalCta: {
    headline: "Your mission starts now",
    highlight: "mission",
    subheading: "7 days. Hand-picked. Real cash on every milestone. Let's go.",
    ctaLabel: "Go to the leaderboard",
    claimTitle: "How to claim",
    steps: [
      { icon: "Unlock", title: "Sign up", desc: "Log in to the leaderboard during the campaign window (10-16 July) to get $10." },
      { icon: "Target", title: "Drive subscriptions", desc: "Every subscription moves you up the milestone ladder." },
      { icon: "Trophy", title: "Get paid", desc: "Stack milestone rewards and chase the $7,000 top prize." },
    ],
  },
};
